import { api } from './api';
import { supabase } from '../lib/supabase';

export interface Asset {
  id: string;
  user_id: string;
  filename: string;
  original_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  width?: number;
  height?: number;
  category: string;
  tags: string[];
  description?: string;
  is_favorite: boolean;
  usage_count: number;
  thumbnail_url?: string;
  created_at: string;
  updated_at: string;
}

export interface UploadAssetData {
  file: File;
  category?: string;
  tags?: string[];
  description?: string;
}

class AssetService {
  async uploadAsset(data: UploadAssetData): Promise<{ asset?: Asset; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      console.log('Uploading asset for user:', user.id);

      // Validate file
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (data.file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
      if (!allowedTypes.includes(data.file.type)) {
        throw new Error('Invalid file type. Only JPG, PNG, SVG, and WebP are allowed.');
      }

      const formData = new FormData();
      formData.append('file', data.file);
      if (data.category) {
        formData.append('category', data.category);
      }
      if (data.tags) {
        formData.append('tags', JSON.stringify(data.tags));
      }
      if (data.description) {
        formData.append('description', data.description);
      }

      const response = await api.post<{ success: boolean; asset: Asset }>('/api/media/assets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const asset = response.data.asset;

      console.log('Asset saved successfully:', asset);
      return { asset };
    } catch (error: any) {
      console.error('Error uploading asset:', error);
      return { error: error.message || 'Failed to upload asset' };
    }
  }

  async getUserAssets(options?: {
    limit?: number;
    offset?: number;
    category?: string;
    favoritesOnly?: boolean;
    searchQuery?: string;
  }): Promise<{ assets: Asset[]; count: number; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let query = supabase
        .from('assets')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (options?.category && options.category !== 'all') {
        query = query.eq('category', options.category);
      }

      if (options?.favoritesOnly) {
        query = query.eq('is_favorite', true);
      }

      if (options?.searchQuery) {
        query = query.or(`original_name.ilike.%${options.searchQuery}%,description.ilike.%${options.searchQuery}%`);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data: assets, count, error } = await query;

      if (error) throw error;

      return { assets: assets || [], count: count || 0 };
    } catch (error: any) {
      console.error('Error fetching assets:', error);
      return { assets: [], count: 0, error: error.message };
    }
  }

  async toggleFavorite(assetId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get current state
      const { data: asset } = await supabase
        .from('assets')
        .select('is_favorite')
        .eq('id', assetId)
        .eq('user_id', user.id)
        .single();

      if (!asset) throw new Error('Asset not found');

      // Toggle favorite
      const { error } = await supabase
        .from('assets')
        .update({ is_favorite: !asset.is_favorite })
        .eq('id', assetId)
        .eq('user_id', user.id);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      return { success: false, error: error.message };
    }
  }

  async updateAsset(assetId: string, updates: {
    category?: string;
    tags?: string[];
    description?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('assets')
        .update(updates)
        .eq('id', assetId)
        .eq('user_id', user.id);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Error updating asset:', error);
      return { success: false, error: error.message };
    }
  }

  async deleteAsset(assetId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      await api.delete(`/api/media/assets/${assetId}`);

      console.log('Asset deleted successfully');
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting asset:', error);
      return { success: false, error: error.message };
    }
  }

  async incrementUsageCount(assetId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Use the increment RPC function if available
      const { error } = await supabase.rpc('increment', { 
        table_name: 'assets',
        row_id: assetId,
        column_name: 'usage_count'
      });

      if (error) {
        // Fallback to manual increment if RPC fails
        const { data: asset } = await supabase
          .from('assets')
          .select('usage_count')
          .eq('id', assetId)
          .eq('user_id', user.id)
          .single();

        if (asset) {
          await supabase
            .from('assets')
            .update({ usage_count: (asset.usage_count || 0) + 1 })
            .eq('id', assetId)
            .eq('user_id', user.id);
        }
      }
    } catch (error) {
      console.error('Error incrementing usage count:', error);
    }
  }
  async getCategories(): Promise<string[]> {
    return ['general', 'logos', 'products', 'backgrounds', 'icons', 'patterns', 'textures'];
  }

  async removeBackground(assetId: string, assetUrl: string): Promise<{ asset?: { id: string; url: string; filename: string }; error?: string }> {
    try {
      console.log('Removing background from asset:', assetId);
      
      // Call the backend API to remove background
      const response = await api.post<{ 
        success: boolean; 
        asset: { id: string; filename: string; url: string; generatedAt: Date }; 
        error?: string 
      }>('/api/mockup/remove-background/asset', { 
        assetId,
        assetUrl 
      });
      
      if (response.data.success && response.data.asset) {
        return { 
          asset: {
            id: response.data.asset.id,
            url: response.data.asset.url,
            filename: response.data.asset.filename
          }
        };
      }
      
      throw new Error(response.data.error || 'Failed to remove background');
    } catch (error: any) {
      console.error('Error removing background:', error);
      return { error: error.message || 'Failed to remove background' };
    }
  }
}

export const assetService = new AssetService();
